import React, { useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import type {
	BackendExperiment,
	ProductEdition,
	SearchExperimentLayer,
} from '@atlassian/search-experiment';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useSearchInputContext } from './SearchInputProvider.tsx';
import { useResultSupplier } from './useResultSupplier.tsx';
import { queryProcessor } from './utils/queryPreprocessing/query-processor.ts';
import type { AdvancedSearchResultType } from './AdvancedSearch-types.ts';
import type { useResultSupplierAdvancedAGGSearchQuery_search_SearchQueryAPI_search_SearchItemConnection_abTest_SearchAbTest } from './__types__/useResultSupplierAdvancedAGGSearchQuery.ts';

export enum QueryState {
	PREQUERY = 'PreQuery',
	ERROR = 'Error',
	POSTQUERY = 'PostQuery',
	LOADING = 'Loading',
}

export type SearchResultsContextType = {
	queryState: QueryState;
	results: AdvancedSearchResultType[];
	searchSessionId: string;
	loading: boolean;
	hasNextPage: boolean;
	loadMore: () => void;
	error: ApolloErrorInterop | undefined;
	numResults: number;
	totalCount?: number | null;
	abTest:
		| useResultSupplierAdvancedAGGSearchQuery_search_SearchQueryAPI_search_SearchItemConnection_abTest_SearchAbTest
		| BackendExperiment
		| null;
	experimentLayers?: SearchExperimentLayer[];
	productEdition?: ProductEdition;
};

export const SearchResultsContext = React.createContext<SearchResultsContextType>({
	queryState: QueryState.PREQUERY,
	results: [],
	searchSessionId: '',
	loading: false,
	hasNextPage: false,
	loadMore: () => {},
	error: undefined,
	numResults: 0,
	abTest: null,
	experimentLayers: undefined,
	productEdition: undefined,
});

export const useSearchResultsContext = () => useContext(SearchResultsContext);

interface SearchResultsProviderProps {
	searchSessionId: string;
	children: ReactNode;
}

export const SearchResultsProvider = ({
	searchSessionId,
	children,
}: SearchResultsProviderProps) => {
	const { cql, queryType, searchQuery, filters, sessionHasUserInput, showSearchAI } =
		useSearchInputContext();

	const { locale } = useSessionData();

	const processedQuery = showSearchAI
		? queryProcessor({
				query: searchQuery,
				locale,
			})
		: searchQuery;

	const resultSupplierContext = useResultSupplier({
		queryType,
		searchQueryText: processedQuery,
		cql,
		searchFilters: filters,
		searchSessionId,
		sessionHasUserInput,
	});

	const queryState =
		(sessionHasUserInput || cql) && !resultSupplierContext.loading && !resultSupplierContext.error
			? QueryState.POSTQUERY
			: resultSupplierContext.loading
				? QueryState.LOADING
				: resultSupplierContext.error
					? QueryState.ERROR
					: QueryState.PREQUERY;

	const contextValue = useMemo(
		() => ({
			...resultSupplierContext,
			queryState,
			searchSessionId,
		}),
		[queryState, resultSupplierContext, searchSessionId],
	);

	return (
		<SearchResultsContext.Provider value={contextValue}>{children}</SearchResultsContext.Provider>
	);
};
